import { Route, Router } from "preact-iso";
import { Controllers, loggedInUser, pageList } from "../types";
import { ErrorRoute } from "./error";

interface RouteBuilderProps {
  pages: pageList;
  currentUser?: loggedInUser;
  onChange?: (url: string) => void;
  topLevelAppRoute?: boolean;
}

export const RouteBuilder = ({
  pages,
  currentUser,
  onChange,
  topLevelAppRoute,
}: RouteBuilderProps) => {
  const paths = Object.keys(pages).filter(
    (path) => pages[path].component in Controllers
  );

  const routes = paths.flatMap((path) => {
    const page = pages[path];
    const routePath = path === "/" ? path : path + "/:rest*";
    return (
      <Route
        path={routePath}
        currentUser={currentUser}
        component={Controllers[page.component]}
        pageTitle={page.title}
        innerHTML={page.body}
      />
    );
  });

  const setPageMeta = (path: string) => {
    const page = pages[path];
    if (page && typeof document !== "undefined") {
      document.body.classList.toggle(
        "top-level",
        !!(topLevelAppRoute ?? page.topLevelApp)
      );
      document.title =
        (page.title || "Home") + " | Ceroc & Modern Jive Adelaide";
    }
  };

  const getPageFromPath = (url: string) => {
    if (url in pages) {
      return url;
    }
    let trimmed = url.replace(/\/$/, "");
    if (trimmed in pages) {
      return trimmed;
    }
    while (trimmed.length > 1 && trimmed.includes("/")) {
      trimmed = trimmed.replace(/\/[^/]*?$/, "");
      if (trimmed in pages) {
        return trimmed;
      }
    }
    return "/";
  };

  const locationPath = location.pathname;
  if (locationPath.startsWith("https://ceroc-adelaide.com.au/")) {
    // Location router requires a domain when prerendering - shouldn't usually see the domain here
    setPageMeta(
      getPageFromPath(
        location.pathname.slice("https://ceroc-adelaide.com.au/".length)
      )
    );
  } else {
    setPageMeta(getPageFromPath(locationPath));
  }

  const onRouteChange = (url: string) => {
    setPageMeta(getPageFromPath(url));
    if (onChange) {
      onChange(url);
    }
  };

  return (
    <Router onRouteChange={onRouteChange}>
      {routes}
      <ErrorRoute type="404" currentUser={currentUser} default />
    </Router>
  );
};
